// @flow
/** @jsx jsx */

import * as React from 'react';

import Text from '../Text';
import { createKeyHandler } from '@zapier/common-utils';
import { Colors, Duration } from '@zapier/theme';
import { css, jsx } from '@emotion/core';

type Props = {
  /** contents of ToggleButton */
  children: React.Node,
  /** Show button in selected state */
  isSelected: boolean,
  /** handle click events */
  onClick: ({ value: any }) => void,
  /** Value to send to onClick when clicked */
  value: any,
};

const toggleButtonBaseCss = css({
  backgroundColor: Colors.storm,
  borderRight: `1px solid ${Colors.adamantium}`,
  cursor: 'pointer',
  outline: 'none',
  padding: '7px 20px 10px 20px',
  textAlign: 'center',
  transition: `background-color ${Duration.short} ease-in-out`,

  ':last-of-type': {
    borderRight: 'none',
  },
  ':only-of-type': {
    border: `1px solid ${Colors.adamantium}`,
    borderRadius: '4px',
  },
  '&:hover': {
    backgroundColor: Colors.theme.secondary.dark,
  },
});

const isSelectedCss = css({
  backgroundColor: Colors.white,
  '&:hover': {
    backgroundColor: Colors.white,
  },
});

/**
 * A button that can toggle between the on / off states
 */
const ToggleButton = ({ children, isSelected, onClick, value }: Props) => {
  const toggleButtonCss = css(toggleButtonBaseCss, isSelected && isSelectedCss);

  return (
    <span
      aria-pressed="false"
      css={toggleButtonCss}
      onClick={() => onClick({ value })}
      onKeyDown={createKeyHandler(['enter'], () => onClick({ value }))}
      role="button"
      tabIndex="0"
    >
      <Text type="subsection-head">{children}</Text>
    </span>
  );
};

export default ToggleButton;
